import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Star,
  Heart,
  Gift,
  Sparkles,
  Calendar,
  ShoppingBag,
  ArrowRight,
  Clock,
  Flower2,
  PartyPopper,
  Baby,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/Auth";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { addToCart as addToCartAction } from "@/store/slices/cartSlice";
import {
  fetchAllProducts,
  selectAllProducts,
  selectProductsLoading,
} from "@/store/slices/productSlice";
import { ProductListSkeleton } from "@/components/products/skeletons";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency } from "@/utils/format";
import Seo from "@/seo/seo";

// Event configurations with themes
const GIFT_EVENTS = [
  {
    id: "valentines",
    name: "Valentine's Day",
    icon: Heart,
    description: "Show your love with the perfect skincare gift",
    color: "from-pink-500 to-rose-500",
    bgColor: "bg-gradient-to-br from-pink-50 to-rose-50",
    accentColor: "text-rose-600",
    borderColor: "border-rose-200",
    buttonColor: "bg-rose-500 hover:bg-rose-600",
    date: "February 14",
    tagline: "Love Your Skin, Love Yourself",
  },
  {
    id: "mothers-day",
    name: "Mother's Day",
    icon: Flower2,
    description: "Celebrate the special woman in your life",
    color: "from-purple-500 to-pink-500",
    bgColor: "bg-gradient-to-br from-purple-50 to-pink-50",
    accentColor: "text-purple-600",
    borderColor: "border-purple-200",
    buttonColor: "bg-purple-500 hover:bg-purple-600",
    date: "Second Sunday of May",
    tagline: "Because She Deserves the Best",
  },
  {
    id: "fathers-day",
    name: "Father's Day",
    icon: Users,
    description: "Premium grooming gifts for Dad",
    color: "from-blue-500 to-indigo-500",
    bgColor: "bg-gradient-to-br from-blue-50 to-indigo-50",
    accentColor: "text-blue-600",
    borderColor: "border-blue-200",
    buttonColor: "bg-blue-500 hover:bg-blue-600",
    date: "Third Sunday of June",
    tagline: "Treat Dad Right",
  },
  {
    id: "christmas",
    name: "Christmas",
    icon: Gift,
    description: "Festive gift sets for everyone on your list",
    color: "from-red-500 to-green-500",
    bgColor: "bg-gradient-to-br from-red-50 to-green-50",
    accentColor: "text-red-600",
    borderColor: "border-red-200",
    buttonColor: "bg-red-500 hover:bg-red-600",
    date: "December 25",
    tagline: "The Season of Giving",
  },
  {
    id: "birthday",
    name: "Birthday",
    icon: PartyPopper,
    description: "Make their special day unforgettable",
    color: "from-amber-500 to-orange-500",
    bgColor: "bg-gradient-to-br from-amber-50 to-orange-50",
    accentColor: "text-amber-600",
    borderColor: "border-amber-200",
    buttonColor: "bg-amber-500 hover:bg-amber-600",
    date: "Any Day",
    tagline: "A Gift They'll Remember",
  },
  {
    id: "baby-shower",
    name: "Baby Shower",
    icon: Baby,
    description: "Gentle care for mom and baby",
    color: "from-teal-500 to-cyan-500",
    bgColor: "bg-gradient-to-br from-teal-50 to-cyan-50",
    accentColor: "text-teal-600",
    borderColor: "border-teal-200",
    buttonColor: "bg-teal-500 hover:bg-teal-600",
    date: "Special Occasion",
    tagline: "Gentle Care for New Beginnings",
  },
];

// Price range filters
const PRICE_RANGES = [
  { id: "all", label: "All Prices", min: 0, max: Infinity },
  { id: "under-5000", label: "Under ₦5,000", min: 0, max: 5000 },
  { id: "5000-15000", label: "₦5,000 - ₦15,000", min: 5000, max: 15000 },
  { id: "15000-30000", label: "₦15,000 - ₦30,000", min: 15000, max: 30000 },
  { id: "above-30000", label: "Above ₦30,000", min: 30000, max: Infinity },
];

export default function GiftCollection() {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const [selectedEvent, setSelectedEvent] = useState("valentines");
  const [selectedPriceRange, setSelectedPriceRange] = useState("all");

  // Redux selectors
  const allProducts = useAppSelector(selectAllProducts);
  const loading = useAppSelector(selectProductsLoading);

  // Fetch products on mount
  useEffect(() => {
    dispatch(fetchAllProducts());
  }, [dispatch]);

  // Get current event config
  const currentEvent = GIFT_EVENTS.find((e) => e.id === selectedEvent) || GIFT_EVENTS[0];
  const CurrentIcon = currentEvent.icon;

  // Filter products by price range
  const priceRange = PRICE_RANGES.find((p) => p.id === selectedPriceRange) || PRICE_RANGES[0];
  const filteredProducts = allProducts.filter((product) => {
    const price = product.discount > 0
      ? product.price - (product.price * product.discount) / 100
      : product.price;
    return price >= priceRange.min && price < priceRange.max;
  });

  // Get featured products (first 6)
  const featuredProducts = filteredProducts.slice(0, 6);

  const handleAddToCart = (product) => {
    const hasDiscount = product.discount > 0;
    const finalPrice = hasDiscount
      ? product.price - (product.price * product.discount) / 100
      : product.price;

    dispatch(
      addToCartAction({
        product: {
          ...product,
          productId: product.productId,
          price: Number(finalPrice),
          image: product.image,
        },
        quantity: 1,
        user,
      })
    );
  };

  return (
    <>
      <Seo
        title="Gift Collection | Perfect Gifts for Every Occasion"
        description="Find the perfect skincare and beauty gift for Valentine's Day, Mother's Day, Christmas, and more. Shop curated gift collections."
        type="website"
      />

      {/* Hero Section */}
      <section className={`relative overflow-hidden ${currentEvent.bgColor} transition-all duration-500`}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.8),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(255,255,255,0.6),transparent_50%)]" />
        </div>

        <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className={`inline-flex items-center gap-2 ${currentEvent.accentColor} bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6`}>
              <CurrentIcon className="w-4 h-4" />
              {currentEvent.name} Collection
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Find the{" "}
              <span className={`bg-gradient-to-r ${currentEvent.color} bg-clip-text text-transparent`}>
                Perfect Gift
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {currentEvent.description}. Discover curated gift sets and bestselling products
              that make every occasion special.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                className={`${currentEvent.buttonColor} text-white px-8 py-3 text-lg transition-all`}
                onClick={() => document.getElementById("gift-products")?.scrollIntoView({ behavior: "smooth" })}
              >
                <Gift className="w-5 h-5 mr-2" />
                Shop Gifts
              </Button>
              <Link to="/shop">
                <Button variant="outline" className="px-8 py-3 text-lg border-gray-300">
                  View All Products
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Event Date Badge */}
            <div className={`mt-8 inline-flex items-center gap-2 ${currentEvent.accentColor} text-sm`}>
              <Calendar className="w-4 h-4" />
              <span>{currentEvent.date}</span>
              <span className="mx-2">•</span>
              <span className="font-medium">{currentEvent.tagline}</span>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 opacity-20">
          <Sparkles className={`w-16 h-16 ${currentEvent.accentColor}`} />
        </div>
        <div className="absolute bottom-10 right-10 opacity-20">
          <Gift className={`w-20 h-20 ${currentEvent.accentColor}`} />
        </div>
      </section>

      {/* Event Selection */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
            Shop by Occasion
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {GIFT_EVENTS.map((event) => {
              const EventIcon = event.icon;
              const isActive = selectedEvent === event.id;
              return (
                <button
                  key={event.id}
                  onClick={() => setSelectedEvent(event.id)}
                  className={`group relative p-4 rounded-xl border-2 transition-all duration-300 ${
                    isActive
                      ? `${event.borderColor} ${event.bgColor} shadow-lg scale-105`
                      : "border-gray-200 hover:border-gray-300 hover:shadow-md bg-white"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={`p-3 rounded-full transition-all ${
                        isActive
                          ? `bg-gradient-to-br ${event.color} text-white`
                          : "bg-gray-100 text-gray-600 group-hover:bg-gray-200"
                      }`}
                    >
                      <EventIcon className="w-5 h-5" />
                    </div>
                    <span
                      className={`text-sm font-medium text-center ${
                        isActive ? event.accentColor : "text-gray-700"
                      }`}
                    >
                      {event.name}
                    </span>
                  </div>
                  {isActive && (
                    <div
                      className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-gradient-to-r ${event.color}`}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Price Filter & Products */}
      <section id="gift-products" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-2 ${currentEvent.accentColor} mb-4`}>
              <CurrentIcon className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                {currentEvent.name}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Curated Gift Ideas
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Hand-picked products perfect for gifting. Find something special within your budget.
            </p>
          </div>

          {/* Price Range Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {PRICE_RANGES.map((range) => (
              <Button
                key={range.id}
                onClick={() => setSelectedPriceRange(range.id)}
                variant={selectedPriceRange === range.id ? "default" : "outline"}
                className={
                  selectedPriceRange === range.id
                    ? `${currentEvent.buttonColor} text-white`
                    : "hover:bg-gray-100"
                }
              >
                {range.label}
              </Button>
            ))}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="py-10">
              <div className="text-center mb-8">
                <Skeleton className="w-48 h-8 mx-auto mb-4" />
                <Skeleton className="w-96 h-5 mx-auto" />
              </div>
              <ProductListSkeleton count={6} className="md:grid-cols-3" />
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {featuredProducts.map((product) => {
                const hasDiscount = product.discount > 0;
                const discountedPrice = hasDiscount
                  ? product.price - (product.price * product.discount) / 100
                  : product.price;
                const savings = hasDiscount
                  ? (product.price * product.discount) / 100
                  : 0;

                return (
                  <Card
                    key={product.productId}
                    className={`overflow-hidden border ${currentEvent.borderColor} rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 bg-white group pt-0`}
                  >
                    <CardContent className="p-0 relative">
                      {/* Product Image */}
                      <Link to={`/products/${product.productId}`}>
                        <div className="relative overflow-hidden">
                          <img
                            src={product.image || "/placeholder.svg"}
                            alt={product.name}
                            className="w-full h-56 object-cover transition-transform duration-300 group-hover:scale-105"
                            onError={(e) => {
                              e.target.src = "/placeholder.svg";
                            }}
                          />
                          {/* Gift Badge */}
                          <div className={`absolute top-3 right-3 ${currentEvent.buttonColor} text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1`}>
                            <Gift className="w-3 h-3" />
                            Gift Idea
                          </div>
                          {/* Discount Badge */}
                          {hasDiscount && (
                            <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-md shadow-md">
                              -{product.discount}%
                            </span>
                          )}
                        </div>
                      </Link>

                      <div className="p-5">
                        {/* Ratings */}
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < Math.floor(product.rating || 0)
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-gray-300"
                              }`}
                            />
                          ))}
                          <span className="text-sm text-gray-500 ml-2">
                            ({product.reviews || 0})
                          </span>
                        </div>

                        {/* Product Name */}
                        <h3 className="font-semibold text-lg text-gray-800 mb-2 line-clamp-1">
                          <Link to={`/products/${product.productId}`} className="hover:text-primary transition-colors">
                            {product.name}
                          </Link>
                        </h3>

                        {/* Description */}
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {product.description}
                        </p>

                        {/* Price & Add to Cart */}
                        <div className="flex items-center justify-between">
                          {hasDiscount ? (
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-500 line-through">
                                {formatCurrency(product.price)}
                              </span>
                              <span className="text-xl font-bold text-red-600">
                                {formatCurrency(discountedPrice)}
                              </span>
                              <span className="text-xs text-green-600 font-medium">
                                Save {formatCurrency(savings)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xl font-bold text-gray-900">
                              {formatCurrency(product.price)}
                            </span>
                          )}
                          <Button
                            onClick={() => handleAddToCart(product)}
                            className={`${currentEvent.buttonColor} text-white`}
                            disabled={product.stock_quantity <= 0}
                          >
                            <ShoppingBag className="w-4 h-4 mr-2" />
                            {product.stock_quantity <= 0 ? "Sold Out" : "Add"}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Gift className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No products in this price range
              </h3>
              <p className="text-gray-500 mb-6">
                Try selecting a different price range to find the perfect gift.
              </p>
              <Button onClick={() => setSelectedPriceRange("all")} variant="outline">
                View All Prices
              </Button>
            </div>
          )}

          {/* View All Button */}
          {featuredProducts.length > 0 && (
            <div className="text-center mt-12">
              <Link to="/shop">
                <Button
                  variant="outline"
                  className={`border-2 ${currentEvent.borderColor} ${currentEvent.accentColor} hover:${currentEvent.bgColor} px-8 py-3`}
                >
                  View All Products
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Gift Guide Banner */}
      <section className={`py-16 bg-gradient-to-r ${currentEvent.color}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <Sparkles className="w-12 h-12 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Not Sure What to Gift?
            </h2>
            <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
              Our skincare experts can help you find the perfect gift based on skin type,
              concerns, and preferences. Contact us for personalized recommendations!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact-us">
                <Button className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-3">
                  Get Gift Advice
                </Button>
              </Link>
              <Link to="/faq">
                <Button variant="outline" className="border-white text-white hover:bg-white/20 px-8 py-3">
                  Gift FAQs
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Gift From Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            Why Gift From Us?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-6">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${currentEvent.bgColor} flex items-center justify-center`}>
                <Gift className={`w-8 h-8 ${currentEvent.accentColor}`} />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Beautiful Packaging
              </h3>
              <p className="text-gray-600">
                All gifts come in elegant, ready-to-gift packaging that makes a lasting impression.
              </p>
            </div>

            <div className="text-center p-6">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${currentEvent.bgColor} flex items-center justify-center`}>
                <Clock className={`w-8 h-8 ${currentEvent.accentColor}`} />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Fast Delivery
              </h3>
              <p className="text-gray-600">
                We ensure your gift arrives on time with our express delivery options.
              </p>
            </div>

            <div className="text-center p-6">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${currentEvent.bgColor} flex items-center justify-center`}>
                <Heart className={`w-8 h-8 ${currentEvent.accentColor}`} />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Personal Touch
              </h3>
              <p className="text-gray-600">
                Add a personalized message to make your gift extra special and memorable.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
